import React from "react";
import Box from "@mui/material/Box";
import withStyles from "@mui/styles/withStyles";
import MaterialChip from "@mui/material/Chip";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import { styled } from "@mui/system";

// Breadcrumbs
import Breadcrumbs from "@mui/material/Breadcrumbs";
import { Link } from "react-router-dom";
import Typography from "@mui/material/Typography";

// Search Icon
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";

// Auto Complete Component
import FormControl from "@mui/material/FormControl";
import Autocomplete from "@mui/material/Autocomplete";

// Delete Confirmation Popup
import makeStyles from "@mui/styles/makeStyles";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import Button from "@mui/material/Button";
import SwitchUnstyled, { switchUnstyledClasses } from "@mui/base/SwitchUnstyled";

import Badge from '@mui/material/Badge';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';

const Chip = (props) => {
  const StyledChip = withStyles({
    root: {
      color: props.textcolor,
      backgroundColor: `${props.bgcolor} !important`,
    },
    outlined: {
      color: props.textcolor,
      border: `1px solid ${props.textcolor}`,
      backgroundColor: `transparent !important`,
    },
    icon: {
      color: props.variant === "outlined" ? props.textcolor : "white",
    },
    deleteIcon: {
      color: props.variant === "outlined" ? props.textcolor : "white",
    },
  })(MaterialChip);

  return <StyledChip {...props} />;
};

const CustomSearch = ({ configData }) => {
  return (
    <Box sx={{ display: "flex" }}>
      <TextField
        placeholder={configData.placeHolder}
        size="small"
        value={configData.searchText}
        onChange={(e) => configData.searchHandler(e.target.value)}
      />
      <IconButton className={configData.IconButtonClass} onClick={() => configData.clickHandler()}>
        {configData.searchIcon}
      </IconButton>
    </Box>
  );
};

const AutoCompleteTextBox = ({ configData }) => {
  const alphaWithUnderscore = (str) => str.replace(/[^a-zA-Z0-9_]/gi, "");
  return (
    <Box sx={{ display: "flex" }}>
      <FormControl fullWidth>
        <Autocomplete
          autoSelect
          freeSolo={configData.freeSolo}
          forcePopupIcon={true}
          placeholder={configData.placeHolder}
          options={configData.templateOptions.map((option) => option.label)}
          onSelect={(e) => {
            configData.setTemplate(e.target.value);
          }}
          onInputChange={(e) => {
            if (!configData.freeSolo) configData.setTemplate("");
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              error={configData.maxLengthValidation && !!configData.templateError}
              helperText={configData.maxLengthValidation && configData.templateError}
              inputProps={configData.maxLengthValidation ? { ...params.inputProps, maxLength: 50 } : ""}
              label={configData.label}
              value={configData.template}
              onInput={(e) => {
                if (configData.textValiation) {
                  const validValue = alphaWithUnderscore(e.target.value);
                  e.target.value = validValue;
                  configData.setTemplate(validValue);
                }
              }}
            />
          )}
        />
      </FormControl>
    </Box>
  );
};

// Open Confirm Popup
const ConfirmPopup = ({ configData }) => {
  const useStyles = makeStyles({
    dialogPaper: {
      "& .MuiPaper-root": {
        // maxWidth: "710px",
        // maxWidth: "420px",
      },
      "& .MuiFormHelperText-root": {
        marginLeft: 0,
      },
      "& .MuiGrid-item": {
        paddingTop: "15px",
      },
      "& .MuiGrid-item .MuiTextField-root": {
        marginTop: 0,
      },
      "& .MuiDivider-root": {
        marginTop: "5px",
        marginBottom: "9px",
      },
      "& .MuiDialogActions-root": {
        padding: "0 20px 10px 0",
      },
    },
    formContainer: {
      display: "grid",
      width: "100%",
      textAlign: "left",
      minHeight: "110px",
    },
    container: {
      backgroundColor: "#FFFFFF",
    },
    Toolbar: {
      fontSize: "1.1rem",
      backgroundColor: "white",
      color: "black",
    },
  });

  const classes = useStyles();

  return (
    <Dialog className={classes.dialogPaper} open={true} maxWidth="md" onClose={() => configData.handleCancel()}>
      <AppBar sx={{ position: "relative" }}>
        <Toolbar className={classes.Toolbar}>{configData.popupTitle} </Toolbar>
      </AppBar>
      <Box
        sx={{
          width: "100%",
          maxWidth: configData.maxWidth ? configData.maxWidth : "900px",
        }}
        className={classes.container}
      >
        <Backdrop sx={{ color: "red", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={configData.loadingForConfirm}>
          <CircularProgress color="primary" />
        </Backdrop>
        <Box
          sx={{ width: "100%", minHeight: configData.minHeight ? configData.minHeight : "110px" }}
          component="form"
          className={classes.formContainer}
        >
          <DialogContent>
            {typeof configData.popupMessage === "string" ? (
              <DialogContentText id="alert-dialog-slide-description">{configData.popupMessage}</DialogContentText>
            ) : (
              configData.popupMessage
            )}
          </DialogContent>
          <DialogActions>
            <ButtonUI variant="contained" color="secondary" onClick={configData.handleCancel}>
              Cancel
            </ButtonUI>
            <ButtonUI
              onClick={() => {
                configData.handleClick();
              }}
              variant="contained"
              color="success"
              style={{ marginLeft: 15 }}
              disabled={configData.loadingForConfirm}
            >
              Ok
            </ButtonUI>
          </DialogActions>
        </Box>
      </Box>
    </Dialog>
  );
};

const ButtonUI = (props) => {
  const StyledButton = withStyles({
    root: {
      borderRadius: 20,
      fontSize: 12,
      width: "auto",
      textTransform: "capitalize",
    },
  })(Button);
  return <StyledButton {...props} />;
};

const Switch = (props) => {
  const orange = {
    500: "#ed6c02",
  };

  const grey = {
    400: "#BFC7CF",
    500: "#AAB4BE",
    600: "#6F7E8C",
  };

  const SwitcRoot = styled("span")(
    ({ theme }) => `
    font-size: 0;
    position: relative;
    display: inline-block;
    width: 40px;
    height: 20px;
    margin: 10px;
    cursor: pointer;
    
    &.${switchUnstyledClasses.disabled} {
      opacity: 0.4;
      cursor: not-allowed;
    }
    
    & .${switchUnstyledClasses.track} {
      background: transparent;
      border-radius: 10px;
      display: block;
      height: 100%;
      width: 100%;
      border: 1px solid ${grey[500]};
      position: absolute;
    }
    
    & .${switchUnstyledClasses.thumb} {
      display: block;
      width: 14px;
      height: 14px;
      top: 3px;
      left: 3px;
      border-radius: 16px;
      background-color: ${orange[500]};
      position: relative;
      transition: all 200ms ease;
    }
    
    &.${switchUnstyledClasses.focusVisible} .${switchUnstyledClasses.thumb} {
      background-color: ${grey[500]};
      box-shadow: 0 0 1px 8px rgba(0, 0, 0, 0.25);
    }
    
    &.${switchUnstyledClasses.checked} {
      .${switchUnstyledClasses.thumb} {
      left: 22px;
      top: 3px;
      background-color: #fff;
      }
    
      .${switchUnstyledClasses.track} {
      background: ${orange[500]};
      }
    }
    
    & .${switchUnstyledClasses.input} {
      cursor: inherit;
      position: absolute;
      width: 100%;
      height: 100%;
      top: 0;
      left: 0;
      opacity: 0;
      z-index: 1;
      margin: 0;
    }
    `
  );

  return <SwitchUnstyled component={SwitcRoot} {...props} />;
};

// Prepare Breadcrumb component
const Breadcrumb = ({ configData, loading }) => {
  const useStyles = makeStyles({
    breadCrumb: {
      fontSize: "13px",
      "& p.MuiTypography-root": { fontSize: "13px", color: "rgba(0, 0, 0, 0.87)", wordBreak: "break-all" },
      "& a.MuiTypography-root": { fontSize: "13px", wordBreak: "break-all" },
      "& a.MuiTypography-root:hover": { color: "rgba(0, 0, 0, 0.87)" },
    },
  });
  const classes = useStyles();

  return !loading ? (
    <Breadcrumbs className={classes.breadCrumb} aria-label="breadcrumb" sx={{ flexGrow: 1 }} separator={"/"}>
      {configData.map((item, key) => {
        return item.active ? (
          <Typography key={key}>{item.displayName}</Typography>
        ) : (
          <Link key={key} to={item.link} className={"bread-crum-link"}>
            {item.displayName}
          </Link>
        );
      })}
    </Breadcrumbs>
  ) : null;
};

//Circular progress with label and percentage
const CircularProgressWithLabel = (props) => {
  const { size, value, label, ...otherProps } = props;
  const processSize = size && size === "small" ? 30 : 40;
  const fontSize = size && size === "small" ? "10px" : "12px";

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }} {...otherProps}>
      {label ? (
        <Typography variant="caption" component="div" color="text.secondary">
          {label}
        </Typography>
      ) : (
        ""
      )}
      <Box sx={{ position: "relative", display: "inline-flex" }}>
        <CircularProgress variant="determinate" size={processSize} value={value} />
        <Box
          sx={{
            top: 0,
            left: 0,
            bottom: 0,
            right: 0,
            position: "absolute",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography variant="caption" component="div" color="text.secondary" fontSize={fontSize}>
            {`${Math.round(value)}%`}
          </Typography>
        </Box>
      </Box>
    </div>
  );
};

const IndicationIcon = ({ type, giveMargin = true }) => {
  return (
    <span
      style={{
        height: "7px",
        width: " 7px",
        marginRight: giveMargin ? "10px" : 0,
        backgroundColor: type === "Job" ? "#CC3311" : "#0057E9",
        borderRadius: " 50%",
        display: " inline-block",
      }}
    ></span>
  );
};

const FilesContainer = ({ files, removeFile }) => {
  const useStyles = makeStyles({
    thumbsContainer: {
      display: 'flex',
      flexDirection: 'row',
      flexWrap: 'wrap'
    },
    thumb: {
      display: 'inline-flex',
      borderRadius: 2,
      border: '1px solid #eaeaea',
      marginBottom: 12,
      marginRight: 12,
      width: 100,
      height: 100,
      padding: 4,
      boxSizing: 'border-box'
    },
    thumbInner: {
      display: 'flex',
      minWidth: 0,
      overflow: 'hidden'
    },
    thumbImg: {
      display: 'block',
      width: '100%',
      height: '100%'
    },
    detailsHeader: {
      fontWeight: "normal",
      "&::after": {
        content: "''",
        display: "block",
        width: "40px",
        height: "3px",
        background: "#EC5143",
        margin: "5px 0 0",
        borderRadius: "5px"
      }
    }
  });

  const StyledBadge = styled(Badge)(({ theme }) => ({
    '& .MuiBadge-badge': {
      backgroundColor: '#ffffff',
      padding: 0,
      top: "4%",
      cursor: "pointer",
      '&::after': {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        content: '""'
      },
    }
  }));

  const classes = useStyles();

  let imgFiles = [];
  let otherFiles = [];

  files.map((file) => {
    if (file.lastIndexOf(".") > 0) {
      const fileType = file.substring(file.lastIndexOf(".") + 1, file.length).toLowerCase();

      if (fileType === "jpg" || fileType === "png") imgFiles.push(file);
      else otherFiles.push(file);
    }
  });

  return (
    <>
      <h3 className={classes.detailsHeader}>
        <b> Files List </b>
      </h3>

      <aside className={classes.thumbsContainer}>
        {imgFiles.map((image) => (
          <StyledBadge
            key={image.split("/").pop()}
            overlap="circular"
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            badgeContent={
              <HighlightOffIcon fontSize="small" color="error" />
            }
            onClick={() => removeFile(image)}
          >
            <div className={classes.thumb}>
              <div className={classes.thumbInner}>
                <img
                  className={classes.thumbImg}
                  crossOrigin="anonymous"
                  alt="Job"
                  src={
                    image
                      ? process.env.REACT_APP_API_URL + "/" + image
                      : process.env.REACT_APP_API_URL + "/file/jobs/NoImageAvailable.png"
                  }
                />
              </div>
            </div>
          </StyledBadge>
        ))}
      </aside>

      <aside>
        <ul style={{ marginTop: 0 }}>
          {otherFiles.map((file) => (
            <li key={file}>
              <div style={{ display: "flex", alignItems: "center" }}>
                {file.split("/").pop()}
                <HighlightOffIcon fontSize="small" color="error" style={{ marginLeft: "auto", cursor: "pointer" }} onClick={() => removeFile(file)} />
              </div>
            </li>
          ))}
        </ul>
      </aside>
    </>
  );
};

const PasswordValidationText = () => {
  return(
    <>
      Password length must be between 6 and 15,
      <br />
      and should contain one Upper Case character,
      <br />
      one letter, one number and one special character
    </>
  )
};

export {
  Chip,
  CustomSearch,
  AutoCompleteTextBox,
  ConfirmPopup,
  ButtonUI,
  Switch,
  Breadcrumb,
  CircularProgressWithLabel,
  IndicationIcon,
  FilesContainer,
  PasswordValidationText
};
